import { ReactElement, useEffect, useMemo } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectHasUserCompletedOnboardingPhase,
  selectPartitionedGettingStartedUserOnboardingStepData,
} from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import {
  GettingStartedGroup,
  GettingStartedOnboardingStep,
  OnboardingStepPhase,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { useGettingStartedOnboardingSteps } from './GettingStarted.hooks'
import { Accordion, Icon, Link, Badge, Text } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

const renderGroupContainer = ({
  title,
  headerLink,
  group,
  partitionedSteps,
  gettingStartedSteps,
}: {
  title: string
  headerLink?: ReactElement
  group: GettingStartedGroup
  partitionedSteps: ReturnType<
    typeof selectPartitionedGettingStartedUserOnboardingStepData
  >
  gettingStartedSteps: ReturnType<typeof useGettingStartedOnboardingSteps>
}) => {
  const stepsInGroup = partitionedSteps.onboardingStepsWithGroups.filter(
    (step) => step.group === group
  )
  const groupStepsInTop5 = stepsInGroup.filter((step) =>
    partitionedSteps.topFiveIncompleteStepIds.includes(
      step.onboardingStep.identifier
    )
  )
  const groupStepsTotal = stepsInGroup.length
  const groupStepsCompletedOrSkippedCount = stepsInGroup.filter(
    (step) => step.completedAt || step.skippedAt
  ).length

  if (groupStepsInTop5.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MOSS}>
          <div className="flex gap-2 items-center">
            {title}
            {groupStepsTotal > 1 ? (
              <Badge color="yellow" variant="secondary">
                {groupStepsCompletedOrSkippedCount} / {groupStepsTotal}
              </Badge>
            ) : null}
          </div>
        </Text>
        {headerLink}
      </div>

      {groupStepsInTop5.map((step) => {
        const stepContent = gettingStartedSteps[step.onboardingStep.identifier]
        return stepContent ?? null
      })}
    </div>
  )
}

const gettingStartedItemPriorityDefault = [
  GettingStartedOnboardingStep.yearEndWrapUpSurvey,
  GettingStartedOnboardingStep.complete_1099_survey,
  GettingStartedOnboardingStep.joinGroupOnboardingSession,
  GettingStartedOnboardingStep.connectBusinessBankAccount,
  GettingStartedOnboardingStep.uploadBankStatements,
  GettingStartedOnboardingStep.clarifyUncategorizedTransactions,
  GettingStartedOnboardingStep.setupAutomaticStatementUpload,
]

export const OnboardingItemList = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      fetchUserOnboardingStepsIfNeeded({
        expanded: true,
        alwaysFetch: true,
      })
    )
  }, [dispatch])

  const partitionedSteps = useReselector(
    selectPartitionedGettingStartedUserOnboardingStepData,
    gettingStartedItemPriorityDefault
  )
  const gettingStartedSteps = useGettingStartedOnboardingSteps()

  const getHelpWithHeardSection = useMemo(() => {
    return renderGroupContainer({
      title: 'Get Help with Heard',
      group: GettingStartedGroup.getHelp,
      partitionedSteps,
      gettingStartedSteps,
    })
  }, [partitionedSteps, gettingStartedSteps])
  const setUpYourAccountSection = useMemo(() => {
    return renderGroupContainer({
      title: 'Set up your account',
      group: GettingStartedGroup.accountSetup,
      partitionedSteps,
      gettingStartedSteps,
    })
  }, [partitionedSteps, gettingStartedSteps])
  const bookkeepingSection = useMemo(() => {
    return renderGroupContainer({
      title: 'Expedite your catch-up bookkeeping',
      headerLink: (
        <Link
          newPage
          href="https://support.joinheard.com/hc/en-us/articles/26200231978135/"
        >
          Why is this important?
        </Link>
      ),
      group: GettingStartedGroup.bookkeeping,
      partitionedSteps,
      gettingStartedSteps,
    })
  }, [partitionedSteps, gettingStartedSteps])
  const taxesSection = useMemo(() => {
    return renderGroupContainer({
      title: 'Prepare For Taxes',
      group: GettingStartedGroup.taxes,
      partitionedSteps,
      gettingStartedSteps,
    })
  }, [partitionedSteps, gettingStartedSteps])
  const payrollSection = useMemo(() => {
    return renderGroupContainer({
      title: 'Payroll',
      group: GettingStartedGroup.payroll,
      partitionedSteps,
      gettingStartedSteps,
    })
  }, [partitionedSteps, gettingStartedSteps])

  const completedStepCount = partitionedSteps.stepsCompletedOrSkippedIds.length

  const gettingStartedPhaseComplete = useReselector(
    selectHasUserCompletedOnboardingPhase,
    OnboardingStepPhase.gettingStarted
  )

  return (
    <div className="flex flex-col gap-10">
      {taxesSection}
      {getHelpWithHeardSection}
      {setUpYourAccountSection}
      {bookkeepingSection}
      {payrollSection}
      {partitionedSteps.upcomingStepsCount > 0 && (
        <Text>
          <Icon icon={regular('flag-pennant')} className="mr-2" />
          {partitionedSteps.upcomingStepsCount} upcoming
        </Text>
      )}
      {completedStepCount > 0 && (
        <Accordion
          type="single"
          collapsible
          variant="text"
          defaultValue={
            gettingStartedPhaseComplete ? 'reviewed-steps' : undefined
          }
        >
          <Accordion.Item value="reviewed-steps">
            <Accordion.Trigger>
              Show {completedStepCount} reviewed
            </Accordion.Trigger>
            <Accordion.Content>
              <div className="flex flex-col gap-4">
                {partitionedSteps.stepsCompletedOrSkippedIds.map((stepId) => {
                  const stepContent = gettingStartedSteps[stepId]
                  return stepContent ?? null
                })}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  )
}
